import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
    createOrthodontistProtocol,
    deleteOrthodontistProtocol,
    fetchOrthodontistProtocols,
    fetchProtocolsByPractice,
    updateOrthodontistProtocol,
    type OrthodontistProtocolFields
} from '../services/orthodontistPreferencesProtocols';
import { ORTHODONTIST_PROTOCOLS_KEY, PRACTICE_ORTHODONTIST_PROTOCOLS_KEY } from './queryKeys';

export function useOrthodontistProtocols(orthodontistId: string | undefined) {
    const query = useQuery({
        queryKey: ORTHODONTIST_PROTOCOLS_KEY(orthodontistId ?? ''),
        queryFn: () => fetchOrthodontistProtocols(orthodontistId as string),
        enabled: Boolean(orthodontistId),
        staleTime: 60_000
    });

    return {
        protocols: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError,
        error: query.error
    };
}

export function useProtocolsByPractice(practiceId: string | undefined) {
    const query = useQuery({
        queryKey: PRACTICE_ORTHODONTIST_PROTOCOLS_KEY(practiceId ?? ''),
        queryFn: () => fetchProtocolsByPractice(practiceId as string),
        enabled: Boolean(practiceId),
        staleTime: 60_000
    });

    return {
        protocols: query.data ?? [],
        isLoading: query.isLoading,
        isError: query.isError
    };
}

export function useCreateOrthodontistProtocol(orthodontistId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (fields: OrthodontistProtocolFields) =>
            createOrthodontistProtocol(orthodontistId, fields),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_PROTOCOLS_KEY(orthodontistId) });
        }
    });
}

interface UpdateOrthodontistProtocolArgs {
    id: string;
    fields: Partial<OrthodontistProtocolFields>;
}

export function useUpdateOrthodontistProtocol(orthodontistId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ id, fields }: UpdateOrthodontistProtocolArgs) =>
            updateOrthodontistProtocol(orthodontistId, id, fields),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_PROTOCOLS_KEY(orthodontistId) });
        }
    });
}

export function useDeleteOrthodontistProtocol(orthodontistId: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (id: string) => deleteOrthodontistProtocol(orthodontistId, id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ORTHODONTIST_PROTOCOLS_KEY(orthodontistId) });
        }
    });
}
